import React, {FC, useMemo} from 'react';
import {Box, Col, Row} from 'components/Grid';
import {Typography} from 'components/Typography';
import {ShareButton} from 'components/ShareButton';
import {Breadcrumbs} from 'modules/breadcrumbs/components/Breadcrumbs';
import {WineShopMainInfo} from 'modules/wineshops-list/components/WineShopMainInfo';
import {WineShopLocation} from 'modules/wineshops-list/components/WineShopLocation';
import {WineShopEvents} from 'modules/wineshops-list/components/WineShopEvents';
import {WineShopProducts} from 'modules/wineshops-list/components/WineShopProducts';
import {usePageViewGtag} from 'hooks/analytics/gtag/usePageViewGtag';
import {IEvent, IWineShop} from 'modules/wineshops-list/models';
import {IProductShort} from 'modules/products/types';
import style from './style.module.scss';
import {IBreadcrumb} from 'modules/seo/types';
import {useBySimple} from 'modules/wineshops-list/hooks/useBySimple';
import {ShieldBySimple} from 'modules/wineshops-list/components/ShieldBySimple';
import {EventPortalName} from 'modules/google-analytics/constants';

interface IProps {
    shop: IWineShop;
    products?: IProductShort[];
    events: IEvent[];
    pageTitle: string;
    breadcrumbs?: IBreadcrumb[];
}

const useShopEssentials = (shop: IWineShop | null): string => {
    return useMemo(() => {
        if (!shop) {
            return '';
        }

        const essentials = [`${shop.title}:`];

        if (shop.requisites?.urName) {
            essentials.push(` ${shop.requisites.urName}`);
        }

        if (shop.requisites?.inn) {
            essentials.push(`, ИНН: ${shop.requisites.inn}`);
        }

        if (shop.license?.number) {
            essentials.push(
                `, лицензия: ${shop.license.number} действует с ${shop.license.validityPeriod.startDate} до ${shop.license.validityPeriod.endDate}`,
            );
        }

        if (shop.requisites?.ogrn) {
            essentials.push(`, ОГРН: ${shop.requisites.ogrn}`);
        }

        return essentials.join('');
    }, [shop]);
};

const ShopHeader: FC<{shop: IWineShop; pageTitle: string}> = ({shop, pageTitle}) => {
    const isBySimple = useBySimple(shop);

    return (
        <div>
            {isBySimple && <ShieldBySimple />}
            <Typography className={style.header} element={'h1'} itemProp={'name'}>
                {pageTitle}
            </Typography>
        </div>
    );
};

const BreadcrumbsSection: FC<{shop: IWineShop; breadcrumbs?: IBreadcrumb[]}> = ({breadcrumbs = []}) => {
    if (0 === breadcrumbs.length) {
        return null;
    }

    return (
        <Box>
            <Breadcrumbs breadcrumbs={breadcrumbs} />
        </Box>
    );
};

export const WineShop: FC<IProps> = ({shop, products = [], events = [], pageTitle, breadcrumbs = []}) => {
    usePageViewGtag('VINOTHEQUE_PAGE', EventPortalName.Wineshop, 'Vinotheques', shop?.title);

    const shopEssentials = useShopEssentials(shop);

    if (!shop) {
        return null;
    }

    return (
        <>
            <BreadcrumbsSection shop={shop} breadcrumbs={breadcrumbs} />

            <Box className={style.box} itemScope={false} itemType={'https://schema.org/Store'}>
                <Row>
                    <Col className={style.headerWrapper} sm={12}>
                        <ShopHeader shop={shop} pageTitle={pageTitle} />
                        <ShareButton />
                    </Col>
                </Row>

                <WineShopMainInfo data={shop} />

                {0 < events.length && <WineShopEvents events={events} />}

                <WineShopLocation data={shop} essentials={shopEssentials} />

                {0 < products.length && <WineShopProducts products={products} shop={shop} />}
            </Box>
        </>
    );
};
